import { useEffect, useRef, useState } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';

type ItemType = 'accommodation' | 'activity' | 'package';

export function useGuestReservationTemporaryLock(params: {
  isOpen: boolean;
  itemType: ItemType;
  itemId: string;
  checkIn: string;
  checkOut: string;
}) {
  const { isOpen, itemType, itemId, checkIn, checkOut } = params;

  const sessionIdRef = useRef<string>(
    typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)
  );
  const [lockId, setLockId] = useState<Id<'temporaryReservations'> | null>(null);
  const lockIdRef = useRef<Id<'temporaryReservations'> | null>(null);
  const [availabilityError, setAvailabilityError] = useState<'booked' | 'locked' | 'rate_limited' | null>(null);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  /** Seconds to wait when `availabilityError === 'rate_limited'` (from Convex rate limiter). */
  const [rateLimitRetryAfterSec, setRateLimitRetryAfterSec] = useState<number | null>(null);

  const createLock = useMutation(api.temporaryReservations.create);
  const releaseLock = useMutation(api.temporaryReservations.release);

  useEffect(() => {
    lockIdRef.current = lockId;
  }, [lockId]);

  useEffect(() => {
    if (!isOpen || itemType !== 'accommodation' || !checkIn || !checkOut) {
      const currentLockId = lockIdRef.current;
      if (currentLockId) {
        releaseLock({ id: currentLockId, sessionId: sessionIdRef.current }).catch(() => {});
        setLockId(null);
        lockIdRef.current = null;
      }
      setAvailabilityError(null);
      setLockedUntil(null);
      setRateLimitRetryAfterSec(null);
      return;
    }

    let cancelled = false;
    createLock({
      accommodationId: itemId as Id<'accommodations'>,
      checkIn,
      checkOut,
      sessionId: sessionIdRef.current,
    })
      .then((result) => {
        if (cancelled) return;
        if (result.success) {
          setLockId(result.id);
          lockIdRef.current = result.id;
          setAvailabilityError(null);
          setLockedUntil(null);
          setRateLimitRetryAfterSec(null);
        } else {
          setLockId(null);
          lockIdRef.current = null;
          setAvailabilityError(result.reason);
          if (result.reason === 'rate_limited') {
            setRateLimitRetryAfterSec(result.retryAfter);
            setLockedUntil(null);
          } else {
            setRateLimitRetryAfterSec(null);
            setLockedUntil('lockedUntil' in result ? (result.lockedUntil ?? null) : null);
          }
        }
      })
      .catch(() => {
        if (!cancelled) setAvailabilityError(null);
      });

    return () => {
      cancelled = true;
    };
   
  }, [isOpen, itemType, itemId, checkIn, checkOut]);

  useEffect(() => {
    return () => {
      const currentLockId = lockIdRef.current;
      if (currentLockId) {
        releaseLock({ id: currentLockId, sessionId: sessionIdRef.current }).catch(() => {});
      }
    };
   
  }, []);

  return {
    sessionIdRef,
    lockIdRef,
    lockId,
    setLockId,
    availabilityError,
    lockedUntil,
    rateLimitRetryAfterSec,
  };
}
